import { createAsyncThunk } from '@reduxjs/toolkit';
import { assert } from 'common/utils';
import { selectMyCommunityId } from 'community/api';
import { EventWizardFormSerializedValues } from 'pages/EventWizard/validation';
import { RootState } from 'store';
import { eventApi, eventSeriesApi, selectActiveEventDetails } from 'store/apis/event';
import { newIdempotencyKey } from 'store/apis/event/utils';
import { handleQueryError } from 'store/utils';

import { selectEditEventId } from '../eventWizard.selectors';
import { updateImages } from './updateImages';
import { updateQuestions } from './updateQuestions';

import type { EventApiId } from 'store/apis/event/utils/eventApiId';

export const updateEvent = createAsyncThunk<EventApiId | undefined, EventWizardFormSerializedValues>(
  '[Event Wizard] update button clicked',
  async (payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const eventId = selectEditEventId(state);
    const originalEvent = selectActiveEventDetails(state);
    const communityId = selectMyCommunityId(state);
    // One key for this save, whichever of the three shapes it takes. The tag categories ride on the save,
    // so an automatic retry must resume rather than repeat.
    const idempotencyKey = newIdempotencyKey();

    try {
      assert(eventId, 'Event ID is not present');
      assert(originalEvent, 'Active Event details are not present');

      const relatedImageIds = await dispatch(
        updateImages({ eventId, seriesId: payload.seriesId, values: payload })
      ).unwrap();

      let { seriesId } = payload;

      // Each save is unwrapped so a failure reaches the catch below. Previously the series update and the
      // single-event update were dispatched without unwrapping, which discarded their errors -- and now
      // that the tag-category write rides on them, a discarded error would lose the categories silently.
      if (seriesId) {
        await dispatch(
          eventSeriesApi.endpoints.updateEventSeries.initiate({ relatedImageIds, idempotencyKey, ...payload })
        ).unwrap();
      } else if (payload.schedule.length > 1) {
        assert(communityId, 'Community ID is not present');
        const { id } = await dispatch(
          eventSeriesApi.endpoints.convertSingleEventToSeries.initiate({
            communityId,
            id: eventId,
            relatedImageIds,
            idempotencyKey,
            ...payload,
          })
        ).unwrap();
        seriesId = id;
      } else {
        await dispatch(
          eventApi.endpoints.updateEvent.initiate({ id: eventId, relatedImageIds, idempotencyKey, ...payload })
        ).unwrap();
      }

      // The backend fans the tag categories across every event in the series itself, so there is no longer
      // any need to enumerate the occurrence ids here (which is what the old series refetch was for).
      await dispatch(updateQuestions({ eventId, seriesId, values: payload })).unwrap();

      return eventId;
    } catch (error) {
      handleQueryError(
        error,
        dispatch,
        'eventWizardListener.updateEventButton.clicked',
        'Error occurred while updating the Event'
      );
      // Rethrow so the save rejects: resolving here fired the success toast and redirect on top of the error.
      throw error;
    }
  }
);
